import React, { createContext, useContext, useState } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';

import Toast from '@dashboard/components/Toast';

const ToastStateContext = createContext();

function generateId() {
  return Math.random().toString(36).substr(2, 9);
}

function ToastProvider({ children }) {
  const defaultDuration = 1500;
  const [toasts, setToasts] = useState([]);

  /**
   * @param {Object} options
   * @param {string} options.title
   * @param {string} options.description
   * @param {'success'|'error'|'alert'} options.type
   * @param {number} options.duration
   */
  function emitToast({ title, description, type = 'success', duration = defaultDuration }) {
    const toast = {
      title: title,
      description: description,
      type,
      id: generateId(),
      duration,
    };

    setToasts([...toasts, toast]);

    // remove toast after duration
    const timeout = setTimeout(() => {
      setToasts(toasts.filter(t => t.id !== toast.id));
    }, duration);

    return () => clearTimeout(timeout);
  }

  return (
    <ToastPrimitive.Provider duration={defaultDuration} swipeDirection="right">
      <ToastStateContext.Provider value={{ toasts, setToasts, emitToast }}>
        {children}

        {toasts.map(toast => (
          <Toast key={toast.id} variant={toast.type} duration={toast.duration}>
            <Toast.Title>{toast.title}</Toast.Title>
            {toast.description && <Toast.Description>{toast.description}</Toast.Description>}
          </Toast>
        ))}
      </ToastStateContext.Provider>

      <ToastPrimitive.Viewport className="fixed top-3 right-3 mx-auto z-[100] flex flex-col-reverse gap-y-4 w-80" />
    </ToastPrimitive.Provider>
  );
}

function useToast() {
  const context = useContext(ToastStateContext);

  if (context === undefined) {
    throw new Error('useToastState must be used within a ToastProvider');
  }

  return context;
}

export { ToastProvider, useToast };
